import { createContext, useContext } from 'react';
import type { CallerTenant } from '../api/types';
import { useIsAdministrator } from '../auth/useCallerContext';

/** Mirrors the language and colour-mode keys, so the user's session choices sit side by side. */
export const tenantStorageKey = 'algobridge.tenant';

/** Shown wherever a caller with no tenant at all would otherwise see an empty page. */
export const noTenantsMessageKey = 'tenants.noneAssigned';

/** Shown to an administrator in the same places: they reach every tenant, they just have to pick. */
export const selectTenantPromptKey = 'tenants.selectPrompt';

/**
 * Which message a tenant-dependent page or nav entry shows while no tenant is selected. An
 * administrator merely has not picked one — the toolbar fixes that — while a non-administrator with
 * nothing selected has nothing assigned at all: with exactly one tenant it is auto-selected, and
 * with more the chooser stops them before any page renders.
 */
export function useNoTenantMessageKey(): typeof selectTenantPromptKey | typeof noTenantsMessageKey {
  return useIsAdministrator() ? selectTenantPromptKey : noTenantsMessageKey;
}

export function readStoredTenant(): string | null {
  try {
    return localStorage.getItem(tenantStorageKey);
  } catch {
    // Storage can be unavailable (private browsing); the session then simply starts unselected.
    return null;
  }
}

export function storeTenant(tenantGid: string | null): void {
  try {
    if (tenantGid === null) localStorage.removeItem(tenantStorageKey);
    else localStorage.setItem(tenantStorageKey, tenantGid);
  } catch {
    // As above — failing to remember the choice must not break making it.
  }
}

/**
 * The pure core of start-up selection: a stored tenant the caller still holds wins; a
 * non-administrator with exactly one tenant needs no asking (the server auto-selects the same way);
 * anything else starts unselected. Never picks a substitute for a revoked selection — a tenant is
 * chosen by a person, not inherited by position in a list.
 */
export function initialSelection(
  stored: string | null,
  tenants: readonly CallerTenant[],
  isAdministrator: boolean,
): string | null {
  if (stored !== null && tenants.some((tenant) => tenant.tenantGid === stored)) return stored;
  if (!isAdministrator && tenants.length === 1) return tenants[0]!.tenantGid;

  return null;
}

export interface TenantSelectionValue {
  /** Every tenant the caller may choose from, as the API listed them. */
  tenants: CallerTenant[];
  selectedTenantGid: string | null;
  selectedTenant: CallerTenant | null;
  selectTenant: (tenantGid: string) => void;
  clearSelection: () => void;
}

export const TenantSelectionContext = createContext<TenantSelectionValue | null>(null);

export function useSelectedTenant(): TenantSelectionValue {
  const value = useContext(TenantSelectionContext);
  if (!value) throw new Error('useSelectedTenant must be used inside a TenantProvider.');

  return value;
}
